import { useEffect, useRef } from "react";

/**
 * CelestialCanvas
 * vault 解錠後のホームページに重なる全画面 Canvas。
 * ・天使の羽根シルエット（光の輪郭）がゆっくり漂う
 * ・精霊の光球が浮遊する
 * ・金白の光の粒子が降り注ぐ
 * ・温かい光の柱が画面を包む
 */

interface Angel {
  x: number; y: number;
  vx: number; vy: number;
  opacity: number;
  scale: number;
  phase: number;
  phaseSpeed: number;
  wingSpan: number;
}

interface Spirit {
  x: number; y: number;
  vx: number; vy: number;
  r: number;
  opacity: number;
  phase: number;
  phaseSpeed: number;
  hue: number;
  trail: { x: number; y: number }[];
}

interface LightMote {
  x: number; y: number;
  vy: number;
  vx: number;
  opacity: number;
  size: number;
  phase: number;
  hue: number;
}

export default function CelestialCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animRef = useRef<number>(0);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    const resize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    // ── 天使（羽根シルエット）──
    const angels: Angel[] = Array.from({ length: 5 }, () => ({
      x: Math.random() * (canvas.width || 1920),
      y: Math.random() * (canvas.height || 1080),
      vx: (Math.random() - 0.5) * 0.12,
      vy: -(0.05 + Math.random() * 0.1),
      opacity: 0.03 + Math.random() * 0.06,
      scale: 0.6 + Math.random() * 0.8,
      phase: Math.random() * Math.PI * 2,
      phaseSpeed: 0.005 + Math.random() * 0.008,
      wingSpan: 60 + Math.random() * 80,
    }));

    // ── 精霊（光球＋軌跡）──
    const spirits: Spirit[] = Array.from({ length: 12 }, () => ({
      x: Math.random() * (canvas.width || 1920),
      y: Math.random() * (canvas.height || 1080),
      vx: (Math.random() - 0.5) * 0.4,
      vy: -(0.1 + Math.random() * 0.3),
      r: 2 + Math.random() * 4,
      opacity: 0.15 + Math.random() * 0.35,
      phase: Math.random() * Math.PI * 2,
      phaseSpeed: 0.015 + Math.random() * 0.02,
      hue: 35 + Math.random() * 30,
      trail: [],
    }));

    // ── 光の粒子（降り注ぐ）──
    const motes: LightMote[] = Array.from({ length: 80 }, () => ({
      x: Math.random() * (canvas.width || 1920),
      y: Math.random() * (canvas.height || 1080),
      vy: -(0.2 + Math.random() * 0.5),
      vx: (Math.random() - 0.5) * 0.15,
      opacity: 0.1 + Math.random() * 0.4,
      size: 0.5 + Math.random() * 1.5,
      phase: Math.random() * Math.PI * 2,
      hue: 40 + Math.random() * 20,
    }));

    // ── 天使の羽根を描く ──
    function drawAngel(
      ctx: CanvasRenderingContext2D,
      x: number, y: number,
      scale: number, opacity: number,
      phase: number, wingSpan: number
    ) {
      const wingFlap = Math.sin(phase) * 0.15;
      const ws = wingSpan * scale;

      ctx.save();
      ctx.translate(x, y);
      ctx.globalAlpha = opacity;

      // 左翼
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.bezierCurveTo(
        -ws * 0.4, -ws * (0.3 + wingFlap),
        -ws * 0.9, -ws * (0.1 + wingFlap * 0.5),
        -ws, ws * 0.15
      );
      ctx.bezierCurveTo(
        -ws * 0.7, ws * 0.05,
        -ws * 0.3, -ws * 0.05,
        0, 0
      );
      const leftGrd = ctx.createLinearGradient(-ws, 0, 0, 0);
      leftGrd.addColorStop(0, `rgba(255, 240, 200, 0)`);
      leftGrd.addColorStop(0.4, `rgba(255, 230, 160, ${opacity * 0.6})`);
      leftGrd.addColorStop(1, `rgba(255, 220, 120, 0)`);
      ctx.fillStyle = leftGrd;
      ctx.fill();

      // 右翼
      ctx.beginPath();
      ctx.moveTo(0, 0);
      ctx.bezierCurveTo(
        ws * 0.4, -ws * (0.3 + wingFlap),
        ws * 0.9, -ws * (0.1 + wingFlap * 0.5),
        ws, ws * 0.15
      );
      ctx.bezierCurveTo(
        ws * 0.7, ws * 0.05,
        ws * 0.3, -ws * 0.05,
        0, 0
      );
      const rightGrd = ctx.createLinearGradient(0, 0, ws, 0);
      rightGrd.addColorStop(0, `rgba(255, 220, 120, 0)`);
      rightGrd.addColorStop(0.6, `rgba(255, 230, 160, ${opacity * 0.6})`);
      rightGrd.addColorStop(1, `rgba(255, 240, 200, 0)`);
      ctx.fillStyle = rightGrd;
      ctx.fill();

      // 光の輪（頭上）
      const haloR = ws * 0.18;
      const haloGrd = ctx.createRadialGradient(0, -ws * 0.45, 0, 0, -ws * 0.45, haloR);
      haloGrd.addColorStop(0, `rgba(255, 240, 180, ${opacity * 0.8})`);
      haloGrd.addColorStop(0.5, `rgba(255, 220, 120, ${opacity * 0.3})`);
      haloGrd.addColorStop(1, `rgba(255, 200, 80, 0)`);
      ctx.beginPath();
      ctx.arc(0, -ws * 0.45, haloR, 0, Math.PI * 2);
      ctx.fillStyle = haloGrd;
      ctx.fill();

      // 輪の縁
      ctx.beginPath();
      ctx.arc(0, -ws * 0.45, haloR * 0.85, 0, Math.PI * 2);
      ctx.strokeStyle = `rgba(255, 230, 150, ${opacity * 0.4})`;
      ctx.lineWidth = 0.5;
      ctx.stroke();

      ctx.globalAlpha = 1;
      ctx.restore();
    }

    // ── 精霊の光球を描く ──
    function drawSpirit(
      ctx: CanvasRenderingContext2D,
      spirit: Spirit
    ) {
      // 軌跡
      if (spirit.trail.length > 1) {
        for (let i = 1; i < spirit.trail.length; i++) {
          const t = i / spirit.trail.length;
          ctx.beginPath();
          ctx.moveTo(spirit.trail[i - 1].x, spirit.trail[i - 1].y);
          ctx.lineTo(spirit.trail[i].x, spirit.trail[i].y);
          ctx.strokeStyle = `hsla(${spirit.hue}, 80%, 75%, ${t * spirit.opacity * 0.3})`;
          ctx.lineWidth = spirit.r * t * 0.5;
          ctx.stroke();
        }
      }

      // 光球本体
      const pulse = Math.sin(spirit.phase) * 0.3 + 0.7;
      const r = spirit.r * pulse;
      const grd = ctx.createRadialGradient(spirit.x, spirit.y, 0, spirit.x, spirit.y, r * 3);
      grd.addColorStop(0, `hsla(${spirit.hue}, 90%, 90%, ${spirit.opacity})`);
      grd.addColorStop(0.3, `hsla(${spirit.hue}, 80%, 70%, ${spirit.opacity * 0.6})`);
      grd.addColorStop(0.7, `hsla(${spirit.hue}, 70%, 60%, ${spirit.opacity * 0.2})`);
      grd.addColorStop(1, `hsla(${spirit.hue}, 60%, 50%, 0)`);
      ctx.beginPath();
      ctx.arc(spirit.x, spirit.y, r * 3, 0, Math.PI * 2);
      ctx.fillStyle = grd;
      ctx.fill();

      // 中心の輝点
      ctx.beginPath();
      ctx.arc(spirit.x, spirit.y, r * 0.4, 0, Math.PI * 2);
      ctx.fillStyle = `hsla(${spirit.hue}, 100%, 95%, ${spirit.opacity * 0.9})`;
      ctx.fill();
    }

    let t = 0;

    function draw() {
      t++;
      const w = canvas.width;
      const h = canvas.height;

      ctx.clearRect(0, 0, w, h);

      // ── 温かい光の柱（背景） ──
      const pillarCount = 4;
      for (let i = 0; i < pillarCount; i++) {
        const px = (w / (pillarCount + 1)) * (i + 1);
        const pulse = Math.sin(t * 0.008 + i * 1.2) * 0.5 + 0.5;
        const pillarGrd = ctx.createLinearGradient(0, 0, 0, h);
        pillarGrd.addColorStop(0, `rgba(255, 230, 160, 0)`);
        pillarGrd.addColorStop(0.3, `rgba(255, 220, 130, ${0.015 + pulse * 0.015})`);
        pillarGrd.addColorStop(0.7, `rgba(255, 200, 100, ${0.01 + pulse * 0.01})`);
        pillarGrd.addColorStop(1, `rgba(255, 180, 80, 0)`);
        ctx.fillStyle = pillarGrd;
        ctx.fillRect(px - 40, 0, 80, h);
      }

      // ── 天使 ──
      angels.forEach((a) => {
        a.phase += a.phaseSpeed;
        a.x += a.vx + Math.sin(a.phase * 0.7) * 0.08;
        a.y += a.vy + Math.cos(a.phase * 0.5) * 0.06;

        // 画面外に出たらリセット
        if (a.y < -200) {
          a.y = h + 100;
          a.x = Math.random() * w;
        }
        if (a.x < -200) a.x = w + 100;
        if (a.x > w + 200) a.x = -100;

        drawAngel(ctx, a.x, a.y, a.scale, a.opacity, a.phase, a.wingSpan);
      });

      // ── 精霊 ──
      spirits.forEach((s) => {
        s.phase += s.phaseSpeed;
        s.x += s.vx + Math.sin(s.phase) * 0.3;
        s.y += s.vy + Math.cos(s.phase * 0.7) * 0.2;

        // 軌跡を記録
        s.trail.push({ x: s.x, y: s.y });
        if (s.trail.length > 20) s.trail.shift();

        // 画面外リセット
        if (s.y < -50) {
          s.y = h + 20;
          s.x = Math.random() * w;
          s.trail = [];
        }
        if (s.x < -50) { s.x = w + 20; s.trail = []; }
        if (s.x > w + 50) { s.x = -20; s.trail = []; }

        // 明滅
        s.opacity = 0.1 + Math.sin(s.phase * 1.3) * 0.15 + 0.15;

        drawSpirit(ctx, s);
      });

      // ── 光の粒子 ──
      motes.forEach((m) => {
        m.phase += 0.02;
        m.x += m.vx + Math.sin(m.phase) * 0.2;
        m.y += m.vy;

        if (m.y < -10) {
          m.y = h + 10;
          m.x = Math.random() * w;
        }

        const alpha = m.opacity * (0.6 + Math.sin(m.phase * 1.5) * 0.4);

        // 十字の輝き（明るい粒子のみ）
        if (m.opacity > 0.3) {
          const crossLen = m.size * 3;
          ctx.beginPath();
          ctx.moveTo(m.x - crossLen, m.y);
          ctx.lineTo(m.x + crossLen, m.y);
          ctx.strokeStyle = `hsla(${m.hue}, 80%, 85%, ${alpha * 0.4})`;
          ctx.lineWidth = 0.5;
          ctx.stroke();
          ctx.beginPath();
          ctx.moveTo(m.x, m.y - crossLen);
          ctx.lineTo(m.x, m.y + crossLen);
          ctx.stroke();
        }

        ctx.beginPath();
        ctx.arc(m.x, m.y, m.size, 0, Math.PI * 2);
        ctx.fillStyle = `hsla(${m.hue}, 80%, 85%, ${alpha})`;
        ctx.fill();
      });

      animRef.current = requestAnimationFrame(draw);
    }

    animRef.current = requestAnimationFrame(draw);
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none"
      style={{
        zIndex: 5,
        width: "100vw",
        height: "100vh",
        mixBlendMode: "screen",
      }}
    />
  );
}
